/**
 * Subscription price, shown on the landing page and the signup form.
 *
 * Currency follows the device locale, the same approach the Doggoes app uses.
 * DISPLAY ONLY: PayFast settles in ZAR, so a foreign card is charged the rand
 * amount and their bank converts. The non-ZAR figures are rounded indicative
 * equivalents of R1 000, not live FX, so they drift as the rand moves —
 * revisit them periodically.
 *
 * PRICE_ZAR must stay in step with {patrolit, payfast_amount} in sys.config
 * and with the JSON-LD offer in index.html. Quoting one number and charging
 * another is the kind of mismatch that ends up in a chargeback.
 */
const EUR_REGIONS = ['AT','BE','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','NL','PT','SK','SI','ES','HR'];

// Common Monetary Area — these all transact in rand.
const ZAR_TIMEZONES = ['Africa/Johannesburg', 'Africa/Maseru', 'Africa/Mbabane'];

export type Currency = 'ZAR' | 'USD' | 'EUR' | 'GBP';

/**
 * TIMEZONE FIRST, then locale.
 *
 * This previously read only the locale, and showed South African visitors
 * "$55 per month · Billed in ZAR" — nonsense to the primary audience, and it
 * quietly undercut local credibility on the one page that has to establish it.
 * Cause: a Cape Town browser reports `en-US` locale and `Africa/Johannesburg`
 * timezone. The locale is a LANGUAGE preference — plenty of South Africans
 * read in en-US or en-GB — whereas the timezone is an actual location signal.
 * Taking "US" off the end of a locale tag and calling it a country is the bug.
 *
 * The fallback is ZAR, not USD. This is a South African product priced in rand
 * and sold, for now, in Cape Town; an unidentified visitor is far likelier to
 * be local than not. Defaulting to dollars got the common case wrong to serve
 * the rare one.
 */
export function detectCurrency(): Currency {
  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    const tz = opts.timeZone ?? '';
    if (ZAR_TIMEZONES.includes(tz)) return 'ZAR';

    const region = (opts.locale ?? '').split('-').pop()?.toUpperCase() ?? '';
    if (region === 'ZA') return 'ZAR';
    if (region === 'GB' || region === 'UK') return 'GBP';
    if (EUR_REGIONS.includes(region)) return 'EUR';
    if (region === 'US' || region === 'CA') return 'USD';

    // Locale gave nothing usable (bare "en", say). Fall back to the timezone's
    // continent before giving up on ZAR.
    if (tz.startsWith('America/')) return 'USD';
    if (tz.startsWith('Europe/'))  return 'EUR';
    return 'ZAR';
  } catch {
    return 'ZAR';
  }
}

const PRICES: Record<Currency, string> = { ZAR: 'R1 000', USD: '$55', EUR: '€50', GBP: '£45' };

export const CURRENCY = detectCurrency();

/** The actual, authoritative price. PayFast settles in rand; everything else
 *  on this page is an indicative conversion. */
export const PRICE_ZAR = PRICES.ZAR;

/**
 * Headline price for this visitor. Foreign currencies carry a "≈" because
 * they are rounded static conversions, not live FX — quoting "$55" flat is a
 * promise about a number we do not control.
 *
 * A South African sees exactly "R1 000": no conversion, no note, no clutter.
 */
export const PRICE = CURRENCY === 'ZAR' ? PRICES.ZAR : `≈${PRICES[CURRENCY]}`;

/**
 * Appended for non-ZAR visitors only, and it names the rand amount rather than
 * just saying "billed in ZAR". That way a MISDETECTED visitor — a South
 * African abroad, anyone whose timezone we read wrong — still sees the real
 * price they will be charged. It makes the currency guess harmless instead of
 * merely unlikely to be wrong.
 */
export const BILLING_NOTE =
  CURRENCY === 'ZAR' ? '' : ` Billed in ZAR at ${PRICES.ZAR}/month.`;
